import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import './css/Fonts.css';
import './css/General.css';
import './css/PageTemplate.css';
import './css/HomeStory.css';
import { AuthProvider } from './contexts/AuthContext';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import MyAuthenticator from './components/Authenticator';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const HomePage = lazy(() => import('./pages/HomePage'));
const ConsultingPage = lazy(() => import('./pages/Consulting'));
const AboutUsPage = lazy(() => import('./pages/AboutUs'));
const TrainingsPage = lazy(() => import('./pages/Trainings'));
const BlogsPage = lazy(() => import('./pages/Blogs'));
const NewsPage = lazy(() => import('./pages/News'));
const ContactPage = lazy(() => import('./pages/Contact'));
const GdprPage = lazy(() => import('./pages/Gdpr'));
const CertificatesPage = lazy(() => import('./pages/Certificates'));
const PreferencesPage = lazy(() => import('./pages/Preferences'));
const GamePage = lazy(() => import('./pages/Game'));

export const PresentationContext = React.createContext(null);

function App() {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    function handleFullscreenChange() {
      console.log('Fullscreen changed:', !!document.fullscreenElement);
      setIsFullscreen(!!document.fullscreenElement);
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // Cleanup
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div className="site-container">
      <Router>
        <HelmetProvider>
          <Helmet>
            <meta
              name="description"
              content="Virtual and in person customized AWS trainings. More info on https://syntaxnebula.com or contact us in email at info@syntaxnebula.com."
            />
            <meta name="robots" content="index,follow" />
            <meta name="google" content="notranslate" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link href="https://fonts.googleapis.com/css?family=Open+Sans&display=swap" rel="stylesheet" />
          </Helmet>
        </HelmetProvider>
        <Suspense fallback={<div>Loading...</div>}>
          <AuthProvider>
            <PresentationContext.Provider value={{ isFullscreen, setIsFullscreen }}>
              <div className="site-wrapper">
                {!isFullscreen && <Header />}
                <main className="content-wrapper">
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/consulting" element={<ConsultingPage />} />
                    <Route path="/aboutus" element={<AboutUsPage />} />
                    <Route path="/training/*" element={<TrainingsPage />} />
                    <Route path="/blog/*" element={<BlogsPage />} />
                    <Route path="/news/*" element={<NewsPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/signin" element={<MyAuthenticator />} />
                    <Route path="/gdpr" element={<GdprPage />} />
                    <Route path="/certificates" element={<CertificatesPage />} />
                    <Route path="/preferences" element={<PreferencesPage />} />
                    <Route path="/game" element={<GamePage />} />
                  </Routes>
                </main>
                {!isFullscreen && <Footer />}
              </div>
            </PresentationContext.Provider>
          </AuthProvider>
          <ToastContainer
            position="top-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </Suspense>
      </Router>
    </div>
  );
}

export default App;
